import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import {
  NavLink,
  Switch,
  useLocation,
  useHistory,
  Redirect,
} from 'react-router-dom'
import { useErrorService, useNotificationService } from '@abroad/components'
import { CreatedQuestsTable } from '../components'
import {
  CreateExperienceMenu,
  ExperiencePageHead,
} from '../components/experienceUI'
import CopyQuestModal from '../components/Quests/CopyQuestModal'
import ExperienceCopyModal from '../components/Quests/ExperienceCopyModal'
import PrivateRoute from '../routes/PrivateRoute'
import API from '../utils/API'
import { UTCformatDateInPlan } from '../utils/date'

const BASE = '/admin/experiences'

const Quests = () => {
  const location = useLocation()
  const history = useHistory()
  const Error = useErrorService()
  const Notification = useNotificationService()
  const isArchive = location.pathname.includes('/archive')

  const [showCopyPicker, setShowCopyPicker] = useState(false)
  const [showCopyModal, setShowCopyModal] = useState(false)
  const [isCopying, setIsCopying] = useState(false)
  const [selectedQuestForCopy, setSelectedQuestForCopy] = useState(null)

  // Home (and other pages) can send openCopyFlow via location.state.
  useEffect(() => {
    if (!(location.state && location.state.openCopyFlow)) {
      return undefined
    }
    setShowCopyPicker(true)
    history.replace({
      pathname: location.pathname,
      search: location.search,
      state: { ...(location.state || {}), openCopyFlow: false },
    })
    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state && location.state.openCopyFlow])

  const handleCopyPickerSelect = (option) => {
    if (!option?.value) return
    setSelectedQuestForCopy({
      value: option.value,
      label: option.label || option.extra?.name || option.extra?.title || '',
      extra: option.extra || option,
    })
    setShowCopyPicker(false)
    setShowCopyModal(true)
  }

  const handleCloseCopyPicker = () => {
    if (!isCopying) setShowCopyPicker(false)
  }

  const handleCloseCopyModal = () => {
    if (isCopying) return
    setShowCopyModal(false)
    setSelectedQuestForCopy(null)
  }

  const handleConfirmCopy = async (
    selectedId,
    selectedQuest,
    questName,
    formValues,
  ) => {
    if (!selectedId || !questName) return
    try {
      setIsCopying(true)
      const body = {
        existingQuestId: selectedId,
        questName: questName.trim(),
        ...(formValues?.questStartDate && {
          questStartDate: UTCformatDateInPlan(formValues.questStartDate),
        }),
        ...(formValues?.questEndDate && {
          questEndDate: UTCformatDateInPlan(formValues.questEndDate),
        }),
        itinerary: formValues.itinerary || [],
      }
      const { data } = await API.AdminAPI.quests.copyQuest(body)
      if (data && data._id) {
        setShowCopyModal(false)
        setSelectedQuestForCopy(null)
        const fromTitle =
          selectedQuest?.name ||
          selectedQuest?.title ||
          selectedQuestForCopy?.label ||
          ''
        try {
          sessionStorage.setItem(
            `experience-copied-${data._id}`,
            JSON.stringify({ copiedFromTitle: fromTitle }),
          )
        } catch (storageError) {
          // ignore storage failures
        }
        history.push({
          pathname: `/admin/experiences/${data._id}/overview`,
          state: {
            prefill: data,
            isNewlyCopied: true,
            copiedFromTitle: fromTitle,
          },
        })
        Notification.showNotification(
          'Experience copied successfully!',
          'success',
        )
      }
    } catch (e) {
      Error.showError(e)
    } finally {
      setIsCopying(false)
    }
  }

  return (
    <div className='experience'>
      <Helmet>
        <title>Experiences | Abroad</title>
        <meta name='title' content='Experiences | Abroad' />
        <meta property='og:title' content='Experiences | Abroad' />
        <meta property='twitter:title' content='Experiences | Abroad' />
        <link
          rel='canonical'
          href={`${process.env.REACT_APP_DOMAIN}/admin/experiences`}
        />
        <meta
          property='og:url'
          content={`${process.env.REACT_APP_DOMAIN}/admin/experiences`}
        />
        <meta
          property='twitter:url'
          content={`${process.env.REACT_APP_DOMAIN}/admin/experiences`}
        />
      </Helmet>
      <div className='experience-page'>
        <ExperiencePageHead title='Experiences'>
          <CreateExperienceMenu onCopyFrom={() => setShowCopyPicker(true)} />
        </ExperiencePageHead>

        <div className='experience-listwrap'>
          <nav className='experience-tabs' aria-label='Experience lists'>
            <NavLink
              to={`${BASE}/active`}
              className='experience-tab'
              activeClassName='experience-tab-active'
              isActive={() => !isArchive}>
              Active
            </NavLink>
            <NavLink
              to={`${BASE}/archive`}
              className='experience-tab'
              activeClassName='experience-tab-active'>
              Archive
            </NavLink>
          </nav>

          <Switch location={location}>
            <PrivateRoute exact path={[`${BASE}/active`, BASE]}>
              <CreatedQuestsTable
                key='active-experiences'
                isArchivedTable={false}
              />
            </PrivateRoute>
            <PrivateRoute exact path={`${BASE}/archive`}>
              <CreatedQuestsTable
                key='archive-experiences'
                isArchivedTable={true}
              />
            </PrivateRoute>
            <Redirect to={`${BASE}/active`} />
          </Switch>
        </div>
      </div>

      <ExperienceCopyModal
        show={showCopyPicker}
        onHide={handleCloseCopyPicker}
        onSelect={handleCopyPickerSelect}
        title='Copy from another experience'
        lead='The new draft receives content, enrollment and participant prep. Participants, payments and inquiries are not copied.'
        isLoadingSelection={isCopying}
      />
      <CopyQuestModal
        show={showCopyModal}
        onHide={handleCloseCopyModal}
        selectedQuest={selectedQuestForCopy}
        onConfirmCopy={handleConfirmCopy}
        isLoading={isCopying}
      />
    </div>
  )
}

export default Quests
